import axios from 'axios';

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1" || "http://localhost:3000/api/v1";

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.clear();
      window.location.href = '/signin';
    }
    return Promise.reject(error);
  }
);

// API service functions
export const authService = {
  validateToken: () => api.get('/user/user-by-token'),
  signOut: () => api.post('/auth/signout'),
};

export const userService = {
  getUserByToken: () => api.get('/user/user-by-token'),
  updateProfile: (data) => api.put('/user/profile', data),
  uploadProfileImage: (formData) => api.post('/user/upload-profile-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const feedService = {
  getWallFeeds: (page = 1, limit = 10) => 
    api.get(`/feed/get-wall-feeds?page=${page}&limit=${limit}`),
  getUserProfile: (userId) => api.get(`/user/?id=${userId}`),
  getUserPosts: (userId, page = 1) => 
    api.get(`/feed/get-all-user-feed-by-user-id?userId=${userId}&page=${page}`),
  getFollowStats: (userId) => 
    api.get(`/feed/get-followers-and-following-count?userId=${userId}`),
  getFollowStatus: (userId) => 
    api.get(`/feed/is-user-followed?userId=${userId}`),
  followUser: ({ followingId }) => 
    api.post('/feed/follow-unfollow-user', { followingId }),
  getFollowers: (userId) => api.get(`/feed/get-all-followers?userId=${userId}`),
  getFollowing: (userId) => api.get(`/feed/get-all-following?userId=${userId}`),
  createPost: (formData) => api.post('/feed/create-post', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  likeUnlikePost: (postId) => api.post('/feed/like-unlike-post', { postId }),
  commentOnPost: (postId, content) => api.post('/feed/comment-on-post', { postId, content }),
  getPostComments: (postId, page = 1, limit = 10) => 
    api.get(`/feed/get-all-comments-on-post?postId=${postId}&page=${page}&limit=${limit}`),
  deletePost: (postId) => api.delete('/feed/delete-post', { data: { id: postId } }),
  searchUsers: (query) => api.get(`/feed/search-for-user-feed-profile-by-username?username=${query}`),
};

// Chat Service for both private and team messaging
export const chatService = {
  // Private Chat Functions
  getAllRooms: () => api.get('/chat/get-all-rooms'),
  getRoomMessages: (roomId, page = 1, limit = 20) => 
    api.get(`/chat/get-all-messages-in-a-room?roomId=${roomId}&page=${page}&limit=${limit}`),
  getRoomUsers: (roomId) => api.get(`/chat/get-all-users-in-a-room?roomId=${roomId}`),
  sendMessage: (roomId, message) => api.post('/chat/send-message', { roomId, message }),
  clearUnreadMessages: (roomId) => api.post('/chat/clear-unread-messages', { roomId }),
  
  // Team Chat Functions
  getTeamRoomMessages: (roomId, page = 1, limit = 20) => 
    api.get(`/team/chat/messages?roomId=${roomId}&page=${page}&limit=${limit}`),
  sendTeamMessage: (roomId, message) => api.post('/team/chat/send-message', { roomId, message }),
  
  // Legacy support
  createRoom: (receiverId) => api.post('/chat/create-room', { receiverId }),
  markAsRead: (roomId) => chatService.clearUnreadMessages(roomId),
};

export const notificationService = {
  getNotifications: (page = 1, limit = 20) => 
    api.get(`/user/notifications?page=${page}&limit=${limit}`),
  markAsRead: (notificationId) => api.put(`/user/notifications/mark-as-read/${notificationId}`),
  markAllAsRead: () => api.put('/user/notifications/mark-all-as-read'),
};

export default api; 
